<script setup lang="ts">
import { onMounted, ref } from 'vue';

import {
  type AccountInfo,
  ApiError,
  claimEmail,
  type GameInfo,
  getGames,
  getGoogleAuthUrl,
  getMe,
  login,
  logout,
  signup,
} from './api';
import { runtimeUrls } from './runtimeUrls';

// View state
type ViewType = 'login' | 'claim';
const currentView = ref<ViewType>('login');

// State
const loading = ref(true);
const account = ref<AccountInfo | null>(null);
const games = ref<GameInfo[]>([]);
const error = ref<string | null>(null);
const successMessage = ref<string | null>(null);

// Login form
const loginEmail = ref('');
const loginPassword = ref('');
const loginLoading = ref(false);
const loginError = ref<string | null>(null);

// Claim form
const claimEmailValue = ref('');
const claimPassword = ref('');
const claimLoading = ref(false);
const claimError = ref<string | null>(null);

/**
 * Load account info and games on mount.
 */
async function loadAccount() {
  loading.value = true;
  error.value = null;

  try {
    account.value = await getMe();
    if (account.value && !account.value.isAnonymous) {
      games.value = await getGames();
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load account';
  } finally {
    loading.value = false;
  }
}

/**
 * Handle login form submission.
 */
async function handleLogin() {
  if (!loginEmail.value || !loginPassword.value) {
    loginError.value = 'Email and password are required';
    return;
  }

  loginLoading.value = true;
  loginError.value = null;

  try {
    await login(loginEmail.value, loginPassword.value);
    // Reload account info after successful login
    await loadAccount();
    // Clear form
    loginEmail.value = '';
    loginPassword.value = '';
    successMessage.value = null;
  } catch (e) {
    loginError.value = e instanceof Error ? e.message : 'Login failed';
  } finally {
    loginLoading.value = false;
  }
}

/**
 * Handle claim form submission.
 * Tries to claim anonymous account first; if no session, falls back to signup.
 */
async function handleClaim() {
  if (!claimEmailValue.value || !claimPassword.value) {
    claimError.value = 'Email and password are required';
    return;
  }

  claimLoading.value = true;
  claimError.value = null;

  try {
    try {
      // Try to claim anonymous account
      await claimEmail(claimEmailValue.value, claimPassword.value);
    } catch (e) {
      // If not authenticated (no anonymous session), fall back to signup
      if (e instanceof ApiError && e.status === 401) {
        await signup(claimEmailValue.value, claimPassword.value);
      } else {
        throw e;
      }
    }

    // Success - switch to login view with message
    claimEmailValue.value = '';
    claimPassword.value = '';
    successMessage.value = 'Account created! Please log in.';
    currentView.value = 'login';
  } catch (e) {
    claimError.value = e instanceof Error ? e.message : 'Failed to create account';
  } finally {
    claimLoading.value = false;
  }
}

/**
 * Handle logout.
 */
async function handleLogout() {
  try {
    await logout();
    account.value = null;
    games.value = [];
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Logout failed';
  }
}

/**
 * Redirect to Google OAuth.
 */
function handleGoogleLogin() {
  window.location.href = getGoogleAuthUrl();
}

/**
 * Switch to claim view.
 */
function showClaimView() {
  currentView.value = 'claim';
  successMessage.value = null;
  claimError.value = null;
}

/**
 * Switch to login view.
 */
function showLoginView() {
  currentView.value = 'login';
  claimError.value = null;
}

/**
 * Get game URL for rejoining.
 */
function getGameUrl(gameId: string): string {
  return runtimeUrls.getGameJoinUrl(gameId);
}

onMounted(loadAccount);
</script>

<template>
  <div class="container">
    <h1>Account</h1>

    <!-- Loading state -->
    <div
      v-if="loading"
      class="loading"
    >
      Loading...
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="error"
    >
      {{ error }}
    </div>

    <!-- Logged in state -->
    <div v-else-if="account && !account.isAnonymous">
      <div class="card">
        <div class="account-info">
          <p class="account-email">
            {{ account.email }}
          </p>
          <p class="account-status">
            {{ account.displayName || 'No display name set' }}
          </p>
        </div>
        <div class="actions">
          <button
            class="btn-secondary"
            @click="handleLogout"
          >
            Logout
          </button>
        </div>
      </div>

      <!-- Games list -->
      <div class="card">
        <h2>Your Games</h2>
        <div
          v-if="games.length === 0"
          class="info"
        >
          You haven't joined any games yet.
        </div>
        <ul
          v-else
          class="games-list"
        >
          <li
            v-for="game in games"
            :key="game.gameId"
            class="game-item"
          >
            <span class="game-id">{{ game.gameId }}</span>
            <a
              :href="getGameUrl(game.gameId)"
              class="game-link"
              target="_blank"
            >
              Rejoin →
            </a>
          </li>
        </ul>
      </div>
    </div>

    <!-- Logged out / anonymous state -->
    <div v-else>
      <!-- Login view -->
      <div
        v-if="currentView === 'login'"
        class="card"
      >
        <h2>Login</h2>

        <div
          v-if="successMessage"
          class="success"
        >
          {{ successMessage }}
        </div>

        <form @submit.prevent="handleLogin">
          <div class="form-group">
            <label for="email">Email</label>
            <input
              id="email"
              v-model="loginEmail"
              type="email"
              autocomplete="email"
            >
          </div>

          <div class="form-group">
            <label for="password">Password</label>
            <input
              id="password"
              v-model="loginPassword"
              type="password"
              autocomplete="current-password"
            >
          </div>

          <div
            v-if="loginError"
            class="error"
          >
            {{ loginError }}
          </div>

          <button
            type="submit"
            class="btn-primary"
            :disabled="loginLoading"
          >
            {{ loginLoading ? 'Logging in...' : 'Login' }}
          </button>
        </form>

        <div class="divider">
          or
        </div>

        <button
          class="btn-secondary"
          @click="handleGoogleLogin"
        >
          Continue with Google
        </button>

        <p class="switch-view">
          Don't have an account?
          <button
            class="btn-link"
            @click="showClaimView"
          >
            Create an account
          </button>
        </p>
      </div>

      <!-- Claim/Signup view -->
      <div
        v-else-if="currentView === 'claim'"
        class="card"
      >
        <h2>Create an account</h2>

        <form @submit.prevent="handleClaim">
          <div class="form-group">
            <label for="claim-email">Email</label>
            <input
              id="claim-email"
              v-model="claimEmailValue"
              type="email"
              autocomplete="email"
            >
          </div>

          <div class="form-group">
            <label for="claim-password">Password</label>
            <input
              id="claim-password"
              v-model="claimPassword"
              type="password"
              autocomplete="new-password"
            >
          </div>

          <div
            v-if="claimError"
            class="error"
          >
            {{ claimError }}
          </div>

          <button
            type="submit"
            class="btn-primary"
            :disabled="claimLoading"
          >
            {{ claimLoading ? 'Creating account...' : 'Create account' }}
          </button>
        </form>

        <div class="divider">
          or
        </div>

        <button
          class="btn-secondary"
          @click="handleGoogleLogin"
        >
          Continue with Google
        </button>

        <p class="switch-view">
          Already have an account?
          <button
            class="btn-link"
            @click="showLoginView"
          >
            Login
          </button>
        </p>
      </div>

      <div
        v-if="account?.isAnonymous"
        class="card"
      >
        <p class="info">
          You're currently playing as a guest. Login to save your progress across devices.
        </p>
      </div>
    </div>
  </div>
</template>
